import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";

export interface Country {
  id: number;
  nome: string;
  sigla?: string | null;
  codigoFone?: string | null;
  codigoIbge?: number | null;
  isAtivo: boolean;
}

export interface CountryInsert {
  nome: string;
  sigla?: string | null;
  codigoFone?: string | null;
  codigoIbge?: number | null;
  isAtivo?: boolean;
}

export const useCountries = () => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["countries"],
    queryFn: async () => {
      const { data } = await apiClient.get<Country[]>("/api/paises");
      return data;
    },
  });

  const activeQuery = useQuery({
    queryKey: ["countries", "active"],
    queryFn: async () => {
      const { data } = await apiClient.get<Country[]>("/api/paises", { params: { ativo: true } });
      return data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (body: CountryInsert) => {
      const { data } = await apiClient.post<Country>("/api/paises", body);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["countries"] });
      toast.success("País criado com sucesso!");
    },
    onError: (error: Error) => {
      toast.error(`Erro ao criar país: ${error.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<CountryInsert> }) => {
      const { data: updated } = await apiClient.put<Country>(`/api/paises/${id}`, data);
      return updated;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["countries"] });
      toast.success("País atualizado com sucesso!");
    },
    onError: (error: Error) => {
      toast.error(`Erro ao atualizar país: ${error.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/api/paises/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["countries"] });
      toast.success("País excluído com sucesso!");
    },
    onError: (error: Error) => {
      toast.error(`Erro ao excluir país: ${error.message}`);
    },
  });

  return {
    countries: query.data,
    activeCountries: activeQuery.data,
    isLoading: query.isLoading,
    error: query.error,
    createCountry: createMutation.mutate,
    updateCountry: updateMutation.mutate,
    deleteCountry: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};
